// src/SuperDisciplines.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from './api';

const SERVICE_TYPES = [
  { value: 'EI', label: 'EI (Early Intervention)' },
  { value: 'PI', label: 'PI (Project Impact)' },
  { value: 'ABA', label: 'ABA (Applied Behavior Analysis)' },
  { value: 'OT', label: 'OT (Occupational Therapy)' },
  { value: 'PT', label: 'PT (Physical Therapy)' },
  { value: 'ST', label: 'ST (Speech Therapy)' },
];

const emptyDisciplineForm = {
  id: null,
  organization_id: '',
  service_type: 'EI',
  name: '',
  description: '',
  is_global_template: 1,
};

const emptyDomainForm = {
  code: '',
  name: '',
  description: '',
  position_order: '',
};

const emptySkillForm = {
  code: '',
  name: '',
  description: '',
  age_range: '',
  position_order: '',
};

function SuperDisciplines() {
  const navigate = useNavigate();

  const [disciplines, setDisciplines] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [selectedDisciplineId, setSelectedDisciplineId] = useState(null);
  const [disciplineForm, setDisciplineForm] = useState(emptyDisciplineForm);
  const [isNewDiscipline, setIsNewDiscipline] = useState(false);
  const [discSaving, setDiscSaving] = useState(false);
  const [discMessage, setDiscMessage] = useState('');

  const [domains, setDomains] = useState([]);
  const [domainLoading, setDomainLoading] = useState(false);
  const [newDomain, setNewDomain] = useState(emptyDomainForm);
  const [domainSavingId, setDomainSavingId] = useState(null);

  const [selectedDomainId, setSelectedDomainId] = useState(null);
  const [skills, setSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [newSkill, setNewSkill] = useState(emptySkillForm);
  const [skillSavingId, setSkillSavingId] = useState(null);

  // Preview tree modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTree, setPreviewTree] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  // Delete confirm modal
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    type: null,   // 'domain' | 'skill'
    target: null, // object
  });

  // ---------- Helpers ----------
  const currentDiscipline =
    disciplines.find((d) => d.id === selectedDisciplineId) || null;

  function showDiscMessage(msg) {
    setDiscMessage(msg);
    if (msg) {
      setTimeout(() => setDiscMessage(''), 2500);
    }
  }

  // ---------- Initial load ----------
  useEffect(() => {
    async function loadAll() {
      try {
        setLoading(true);
        setLoadError('');
        const [discRes, orgRes] = await Promise.all([
          API.get('/super/disciplines'),
          API.get('/super/organizations'),
        ]);
        setDisciplines(discRes.data || []);
        setOrganizations(orgRes.data || []);
      } catch (err) {
        console.error('Error loading disciplines/organizations', err);
        setLoadError('Failed to load disciplines or organizations');
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  }, []);

  // ---------- Handlers: disciplines ----------
  const handleSelectDiscipline = async (disc) => {
    setIsNewDiscipline(false);
    setSelectedDisciplineId(disc.id);
    setDisciplineForm({
      id: disc.id,
      organization_id: disc.organization_id || '',
      service_type: disc.service_type || 'EI',
      name: disc.name || '',
      description: disc.description || '',
      is_global_template: disc.is_global_template ? 1 : 0,
    });

    setDomains([]);
    setSelectedDomainId(null);
    setSkills([]);
    setNewDomain(emptyDomainForm);
    setNewSkill(emptySkillForm);

    await loadDomainsForDiscipline(disc.id);
  };

  const handleNewDiscipline = () => {
    setIsNewDiscipline(true);
    setSelectedDisciplineId(null);
    setDisciplineForm(emptyDisciplineForm);
    setDomains([]);
    setSkills([]);
    setSelectedDomainId(null);
    setNewDomain(emptyDomainForm);
    setNewSkill(emptySkillForm);
  };

  const handleDisciplineFormChange = (field, value) => {
    setDisciplineForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveDiscipline = async () => {
    try {
      setDiscSaving(true);
      const payload = {
        organization_id: disciplineForm.organization_id || null,
        service_type: disciplineForm.service_type,
        name: disciplineForm.name,
        description: disciplineForm.description,
        is_global_template: disciplineForm.organization_id
          ? 0
          : disciplineForm.is_global_template
          ? 1
          : 1,
      };

      let res;
      if (disciplineForm.id) {
        res = await API.put(
          `/super/disciplines/${disciplineForm.id}`,
          payload
        );
      } else {
        res = await API.post('/super/disciplines', payload);
      }
      const saved = res.data;

      // Reload list
      const listRes = await API.get('/super/disciplines');
      setDisciplines(listRes.data || []);

      setIsNewDiscipline(false);
      setSelectedDisciplineId(saved.id);
      setDisciplineForm({
        id: saved.id,
        organization_id: saved.organization_id || '',
        service_type: saved.service_type || 'EI',
        name: saved.name || '',
        description: saved.description || '',
        is_global_template: saved.is_global_template ? 1 : 0,
      });

      showDiscMessage('Discipline saved successfully');
      await loadDomainsForDiscipline(saved.id);
    } catch (err) {
      console.error('Error saving discipline', err);
      showDiscMessage('Failed to save discipline');
    } finally {
      setDiscSaving(false);
    }
  };

  // ---------- Handlers: domains ----------
  const loadDomainsForDiscipline = async (disciplineId) => {
    if (!disciplineId) return;
    try {
      setDomainLoading(true);
      const res = await API.get(
        `/super/disciplines/${disciplineId}/domains`
      );
      const { domains: doms } = res.data || {};
      setDomains(doms || []);

      if (doms && doms.length) {
        let nextDomainId = selectedDomainId;
        const exists =
          nextDomainId &&
          doms.some((d) => d.id === nextDomainId);
        if (!exists) {
          nextDomainId = doms[0].id;
        }
        setSelectedDomainId(nextDomainId);
        if (nextDomainId) {
          await loadSkillsForDomain(nextDomainId);
        } else {
          setSkills([]);
        }
      } else {
        setSelectedDomainId(null);
        setSkills([]);
      }
    } catch (err) {
      console.error('Error loading domains for discipline', err);
    } finally {
      setDomainLoading(false);
    }
  };

  const handleNewDomainChange = (field, value) => {
    setNewDomain((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddDomain = async () => {
    if (!selectedDisciplineId || !disciplineForm.id) {
      showDiscMessage('Save discipline first');
      return;
    }
    if (!newDomain.code || !newDomain.name) {
      showDiscMessage('Domain code and name are required');
      return;
    }
    try {
      setDomainSavingId(-1);
      const payload = {
        code: newDomain.code,
        name: newDomain.name,
        description: newDomain.description,
        position_order: newDomain.position_order
          ? Number(newDomain.position_order)
          : null,
      };
      const res = await API.post(
        `/super/disciplines/${disciplineForm.id}/domains`,
        payload
      );
      const created = res.data;
      setDomains((prev) =>
        [...prev, created].sort(
          (a, b) => (a.position_order || 0) - (b.position_order || 0)
        )
      );
      setNewDomain(emptyDomainForm);
      setSelectedDomainId(created.id);
      showDiscMessage('Domain added');
      await loadSkillsForDomain(created.id);
    } catch (err) {
      console.error('Error adding domain', err);
      showDiscMessage('Failed to add domain');
    } finally {
      setDomainSavingId(null);
    }
  };

  const handleDomainChange = (id, field, value) => {
    setDomains((prev) =>
      prev.map((d) =>
        d.id === id ? { ...d, [field]: value } : d
      )
    );
  };

  const handleSaveDomainRow = async (domain) => {
    try {
      setDomainSavingId(domain.id);
      const payload = {
        code: domain.code,
        name: domain.name,
        description: domain.description,
        position_order: domain.position_order
          ? Number(domain.position_order)
          : 0,
      };
      const res = await API.put(
        `/super/domains/${domain.id}`,
        payload
      );
      const updated = res.data;
      setDomains((prev) =>
        prev
          .map((d) => (d.id === updated.id ? updated : d))
          .sort(
            (a, b) => (a.position_order || 0) - (b.position_order || 0)
          )
      );
      showDiscMessage('Domain saved');
    } catch (err) {
      console.error('Error saving domain', err);
      showDiscMessage('Failed to save domain');
    } finally {
      setDomainSavingId(null);
    }
  };

  const handleSelectDomain = async (domainId) => {
    if (!domainId) {
      setSelectedDomainId(null);
      setSkills([]);
      setNewSkill(emptySkillForm);
      return;
    }
    setSelectedDomainId(domainId);
    setSkills([]);
    setNewSkill(emptySkillForm);
    await loadSkillsForDomain(domainId);
  };

  const askDeleteDomain = (domain, e) => {
    if (e) e.stopPropagation();
    setConfirmDelete({
      open: true,
      type: 'domain',
      target: domain,
    });
  };

  // ---------- Handlers: skills ----------
  const loadSkillsForDomain = async (domainId) => {
    if (!domainId) return;
    try {
      setSkillsLoading(true);
      const res = await API.get(
        `/super/domains/${domainId}/skills`
      );
      setSkills(res.data || []);
    } catch (err) {
      console.error('Error loading skills for domain', err);
    } finally {
      setSkillsLoading(false);
    }
  };

  const handleNewSkillChange = (field, value) => {
    setNewSkill((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddSkill = async () => {
    if (!selectedDomainId) {
      showDiscMessage('Select a domain first');
      return;
    }
    if (!newSkill.code || !newSkill.name) {
      showDiscMessage('Skill code and name are required');
      return;
    }
    try {
      setSkillSavingId(-1);
      const payload = {
        code: newSkill.code,
        name: newSkill.name,
        description: newSkill.description,
        age_range: newSkill.age_range,
        position_order: newSkill.position_order
          ? Number(newSkill.position_order)
          : null,
      };
      const res = await API.post(
        `/super/domains/${selectedDomainId}/skills`,
        payload
      );
      const created = res.data;
      setSkills((prev) =>
        [...prev, created].sort(
          (a, b) => (a.position_order || 0) - (b.position_order || 0)
        )
      );
      setNewSkill(emptySkillForm);
      showDiscMessage('Skill added');
    } catch (err) {
      console.error('Error adding skill', err);
      showDiscMessage('Failed to add skill');
    } finally {
      setSkillSavingId(null);
    }
  };

  const handleSkillChange = (id, field, value) => {
    setSkills((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, [field]: value } : s
      )
    );
  };

  const handleSaveSkillRow = async (skill) => {
    try {
      setSkillSavingId(skill.id);
      const payload = {
        code: skill.code,
        name: skill.name,
        description: skill.description,
        age_range: skill.age_range,
        position_order: skill.position_order
          ? Number(skill.position_order)
          : 0,
        domain_id: skill.domain_id,
      };
      const res = await API.put(
        `/super/skills/${skill.id}`,
        payload
      );
      const updated = res.data;
      setSkills((prev) =>
        prev
          .map((s) => (s.id === updated.id ? updated : s))
          .sort(
            (a, b) => (a.position_order || 0) - (b.position_order || 0)
          )
      );
      showDiscMessage('Skill saved');
    } catch (err) {
      console.error('Error saving skill', err);
      showDiscMessage('Failed to save skill');
    } finally {
      setSkillSavingId(null);
    }
  };

  const askDeleteSkill = (skill) => {
    setConfirmDelete({
      open: true,
      type: 'skill',
      target: skill,
    });
  };

  // ---------- Delete confirm handler ----------
  const handleConfirmDelete = async () => {
    if (!confirmDelete.open || !confirmDelete.type || !confirmDelete.target) {
      setConfirmDelete({ open: false, type: null, target: null });
      return;
    }

    try {
      if (confirmDelete.type === 'domain') {
        const id = confirmDelete.target.id;
        await API.delete(`/super/domains/${id}`);
        const remaining = domains.filter((d) => d.id !== id);
        setDomains(remaining);

        if (selectedDomainId === id) {
          if (remaining.length > 0) {
            const nextId = remaining[0].id;
            setSelectedDomainId(nextId);
            await loadSkillsForDomain(nextId);
          } else {
            setSelectedDomainId(null);
            setSkills([]);
          }
        }
        showDiscMessage('Domain deleted (and its skills)');
      } else if (confirmDelete.type === 'skill') {
        const id = confirmDelete.target.id;
        await API.delete(`/super/skills/${id}`);
        setSkills((prev) => prev.filter((s) => s.id !== id));
        showDiscMessage('Skill deleted');
      }
    } catch (err) {
      console.error('Error deleting', err);
      showDiscMessage('Failed to delete item');
    } finally {
      setConfirmDelete({ open: false, type: null, target: null });
    }
  };

  // ---------- Preview tree ----------
  const openPreviewTree = async () => {
    if (!disciplineForm.id) {
      showDiscMessage('Save or select a discipline first');
      return;
    }
    if (!domains.length) {
      showDiscMessage('No domains defined for this discipline');
      return;
    }

    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const treePromises = domains.map(async (dom) => {
        try {
          const res = await API.get(
            `/super/domains/${dom.id}/skills`
          );
          return {
            ...dom,
            skills: res.data || [],
          };
        } catch (err) {
          console.error('Error loading skills for preview', err);
          return {
            ...dom,
            skills: [],
          };
        }
      });

      const tree = await Promise.all(treePromises);
      tree.sort(
        (a, b) => (a.position_order || 0) - (b.position_order || 0)
      );
      setPreviewTree(tree);
    } catch (err) {
      console.error('Error building preview tree', err);
      setPreviewError('Failed to build preview tree');
    } finally {
      setPreviewLoading(false);
    }
  };

  // ---------- Render helpers ----------
  const renderOrgLabel = (disc) => {
    if (!disc.organization_id)
      return 'VisitOMS (global template)';
    const org = organizations.find(
      (o) => o.id === disc.organization_id
    );
    return org ? org.name : `Org #${disc.organization_id}`;
  };

  const renderConfirmTitleAndText = () => {
    if (!confirmDelete.type || !confirmDelete.target) {
      return { title: '', text: '' };
    }
    if (confirmDelete.type === 'domain') {
      const d = confirmDelete.target;
      return {
        title: `Delete domain "${d.name || d.code}"?`,
        text:
          'Are you sure you want to delete this domain? All skills under this domain will also be deleted. This action cannot be undone.',
      };
    }
    if (confirmDelete.type === 'skill') {
      const s = confirmDelete.target;
      return {
        title: `Delete skill "${s.code}"?`,
        text:
          'Are you sure you want to delete this skill? This action cannot be undone.',
      };
    }
    return { title: '', text: '' };
  };

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-semibold mb-2">
          Disciplines & Skills (Super Admin)
        </h1>
        <p>Loading...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-semibold mb-2">
          Disciplines & Skills (Super Admin)
        </h1>
        <p className="text-red-600">{loadError}</p>
      </div>
    );
  }

  const { title: confirmTitle, text: confirmText } =
    renderConfirmTitleAndText();

  return (
    <div className="flex h-full relative">
      {/* Left column: list of disciplines */}
      <div className="w-72 border-r border-gray-200 flex flex-col">
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <h2 className="font-semibold text-sm">Disciplines</h2>
          <button
            className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
            onClick={handleNewDiscipline}
          >
            + New
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {disciplines.length === 0 && (
            <div className="p-3 text-xs text-gray-500">
              No disciplines yet. Click &ldquo;New&rdquo; to create
              one.
            </div>
          )}
          {disciplines.map((disc) => (
            <button
              key={disc.id}
              onClick={() => handleSelectDiscipline(disc)}
              className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 hover:bg-gray-50 ${
                selectedDisciplineId === disc.id &&
                !isNewDiscipline
                  ? 'bg-blue-50'
                  : ''
              }`}
            >
              <div className="font-semibold flex items-center justify-between">
                <span>{disc.name}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-800 text-white">
                  {disc.service_type}
                </span>
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                {renderOrgLabel(disc)}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right column: edit forms */}
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h1 className="text-lg font-semibold">
              Disciplines, Domains & Skills
            </h1>
            <p className="text-xs text-gray-500">
              Configure discipline templates by service type and
              organization. Domains and skills are used in student
              evaluations.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {disciplineForm.id && (
              <button
                className="text-xs px-3 py-1 rounded border border-gray-300 hover:bg-gray-50"
                onClick={openPreviewTree}
              >
                Preview tree
              </button>
            )}
            <button
              className="text-xs text-blue-600 underline"
              onClick={() => navigate('/super/organizations')}
            >
              &larr; Back to Organizations
            </button>
          </div>
        </div>

        {discMessage && (
          <div className="px-4 pt-2">
            <div className="bg-blue-50 text-blue-700 text-xs px-3 py-2 rounded border border-blue-200">
              {discMessage}
            </div>
          </div>
        )}

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-4 p-4">
          {/* Column 1: Discipline form */}
          <div className="border border-gray-200 rounded-lg p-3 flex flex-col">
            <h2 className="text-sm font-semibold mb-2">
              Discipline
            </h2>

            {!isNewDiscipline && !currentDiscipline && (
              <p className="text-xs text-gray-500">
                Select a discipline on the left or click
                &ldquo;New&rdquo;.
              </p>
            )}

            {(isNewDiscipline || currentDiscipline) && (
              <>
                <label className="block text-xs font-medium mb-1">
                  Scope
                </label>
                <select
                  className="w-full border rounded px-2 py-1 text-xs mb-2"
                  value={disciplineForm.organization_id}
                  onChange={(e) =>
                    handleDisciplineFormChange(
                      'organization_id',
                      e.target.value
                    )
                  }
                >
                  <option value="">
                    VisitOMS global template
                  </option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>

                <label className="block text-xs font-medium mb-1">
                  Service type
                </label>
                <select
                  className="w-full border rounded px-2 py-1 text-xs mb-2"
                  value={disciplineForm.service_type}
                  onChange={(e) =>
                    handleDisciplineFormChange(
                      'service_type',
                      e.target.value
                    )
                  }
                >
                  {SERVICE_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>

                <label className="block text-xs font-medium mb-1">
                  Name
                </label>
                <input
                  className="w-full border rounded px-2 py-1 text-xs mb-2"
                  value={disciplineForm.name}
                  onChange={(e) =>
                    handleDisciplineFormChange(
                      'name',
                      e.target.value
                    )
                  }
                  placeholder="e.g. TipInfant EI template"
                />

                <label className="block text-xs font-medium mb-1">
                  Description
                </label>
                <textarea
                  className="w-full border rounded px-2 py-1 text-xs mb-2 h-20"
                  value={disciplineForm.description}
                  onChange={(e) =>
                    handleDisciplineFormChange(
                      'description',
                      e.target.value
                    )
                  }
                  placeholder="Short description for this discipline"
                />

                {!disciplineForm.organization_id && (
                  <label className="inline-flex items-center text-xs mb-2 mt-1">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={!!disciplineForm.is_global_template}
                      onChange={(e) =>
                        handleDisciplineFormChange(
                          'is_global_template',
                          e.target.checked ? 1 : 0
                        )
                      }
                    />
                    <span>
                      Mark as VisitOMS global template
                    </span>
                  </label>
                )}

                <button
                  className="mt-2 self-start px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                  onClick={handleSaveDiscipline}
                  disabled={
                    discSaving || !disciplineForm.name
                  }
                >
                  {discSaving
                    ? 'Saving...'
                    : 'Save Discipline'}
                </button>
              </>
            )}
          </div>

          {/* Column 2: Domains */}
          <div className="border border-gray-200 rounded-lg p-3 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold">Domains</h2>
              {domainLoading && (
                <span className="text-[10px] text-gray-500">
                  Loading...
                </span>
              )}
            </div>

            {!disciplineForm.id && (
              <p className="text-xs text-gray-500">
                Save a discipline first to add domains.
              </p>
            )}

            {disciplineForm.id && (
              <>
                <div className="border border-dashed border-gray-300 rounded p-2 mb-3 bg-gray-50">
                  <div className="text-[11px] font-semibold mb-1">
                    Add domain
                  </div>
                  <div className="mb-1">
                    <input
                      className="border rounded px-1 py-1 text-[11px] w-full mb-1"
                      placeholder="code (e.g. cognitive)"
                      value={newDomain.code}
                      onChange={(e) =>
                        handleNewDomainChange(
                          'code',
                          e.target.value
                        )
                      }
                    />
                    <input
                      className="border rounded px-1 py-1 text-[11px] w-full mb-1"
                      placeholder="name (e.g. Cognitive)"
                      value={newDomain.name}
                      onChange={(e) =>
                        handleNewDomainChange(
                          'name',
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <textarea
                    className="border rounded px-1 py-1 text-[11px] w-full mb-1"
                    placeholder="Description (optional)"
                    value={newDomain.description}
                    onChange={(e) =>
                      handleNewDomainChange(
                        'description',
                        e.target.value
                      )
                    }
                  />
                  <div className="flex items-center gap-2">
                    <input
                      className="border rounded px-1 py-1 text-[11px] w-20"
                      placeholder="Order"
                      value={newDomain.position_order}
                      onChange={(e) =>
                        handleNewDomainChange(
                          'position_order',
                          e.target.value
                        )
                      }
                    />
                    <button
                      className="ml-auto text-[11px] px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                      onClick={handleAddDomain}
                      disabled={domainSavingId === -1}
                    >
                      {domainSavingId === -1
                        ? 'Adding...'
                        : 'Add'}
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {domains.length === 0 &&
                    !domainLoading && (
                      <p className="text-xs text-gray-500">
                        No domains yet. Add at least
                        cognitive/language/gross_motor/fine_motor/social/self_help.
                      </p>
                    )}

                  {domains.map((dom) => (
                    <div
                      key={dom.id}
                      className={`border rounded px-2 py-2 mb-2 text-xs cursor-pointer shadow-sm ${
                        selectedDomainId === dom.id
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-white'
                      }`}
                      onClick={() =>
                        handleSelectDomain(dom.id)
                      }
                    >
                      {/* Code */}
                      <div className="mb-1">
                        <label className="text-[10px] text-gray-500 block mb-0.5">
                          Code
                        </label>
                        <input
                          className="border rounded px-1 py-0.5 text-[11px] w-32"
                          value={dom.code}
                          onClick={(e) =>
                            e.stopPropagation()
                          }
                          onChange={(e) =>
                            handleDomainChange(
                              dom.id,
                              'code',
                              e.target.value
                            )
                          }
                        />
                      </div>

                      {/* Name */}
                      <div className="mb-1">
                        <label className="text-[10px] text-gray-500 block mb-0.5">
                          Name
                        </label>
                        <input
                          className="border rounded px-1 py-0.5 text-[11px] w-full"
                          value={dom.name}
                          onClick={(e) =>
                            e.stopPropagation()
                          }
                          onChange={(e) =>
                            handleDomainChange(
                              dom.id,
                              'name',
                              e.target.value
                            )
                          }
                        />
                      </div>

                      {/* Description + Order */}
                      <div className="flex items-start gap-2 mb-1">
                        <div className="flex-1">
                          <label className="text-[10px] text-gray-500 block mb-0.5">
                            Description
                          </label>
                          <textarea
                            className="border rounded px-1 py-0.5 text-[11px] w-full"
                            value={dom.description || ''}
                            onClick={(e) =>
                              e.stopPropagation()
                            }
                            onChange={(e) =>
                              handleDomainChange(
                                dom.id,
                                'description',
                                e.target.value
                              )
                            }
                            placeholder="Description"
                          />
                        </div>
                        <div className="w-16">
                          <label className="text-[10px] text-gray-500 block mb-0.5">
                            Order
                          </label>
                          <input
                            className="border rounded px-1 py-0.5 text-[11px] w-full"
                            value={dom.position_order || ''}
                            onClick={(e) =>
                              e.stopPropagation()
                            }
                            onChange={(e) =>
                              handleDomainChange(
                                dom.id,
                                'position_order',
                                e.target.value
                              )
                            }
                            placeholder="#"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 mt-1">
                        <button
                          className="text-[11px] px-2 py-0.5 rounded border border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-60"
                          onClick={(e) => askDeleteDomain(dom, e)}
                          disabled={domainSavingId === dom.id}
                        >
                          Delete
                        </button>
                        <button
                          className="text-[11px] px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveDomainRow(dom);
                          }}
                          disabled={domainSavingId === dom.id}
                        >
                          {domainSavingId === dom.id
                            ? 'Saving...'
                            : 'Save'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Column 3: Skills */}
          <div className="border border-gray-200 rounded-lg p-3 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold">
                  Skills
                </h2>
                {disciplineForm.id &&
                  domains.length > 0 && (
                    <select
                      className="border rounded px-2 py-1 text-xs"
                      value={selectedDomainId || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        const domainId = val
                          ? Number(val)
                          : null;
                        handleSelectDomain(domainId);
                      }}
                    >
                      <option value="">
                        Select domain…
                      </option>
                      {domains.map((dom) => (
                        <option
                          key={dom.id}
                          value={dom.id}
                        >
                          {dom.name} ({dom.code})
                        </option>
                      ))}
                    </select>
                  )}
              </div>
              {skillsLoading && (
                <span className="text-[10px] text-gray-500">
                  Loading...
                </span>
              )}
            </div>

            {!selectedDomainId && (
              <p className="text-xs text-gray-500">
                Select a domain (in Domains list or from the
                dropdown) to view and edit its skills.
              </p>
            )}

            {selectedDomainId && (
              <>
                <div className="border border-dashed border-gray-300 rounded p-2 mb-3 bg-gray-50">
                  <div className="text-[11px] font-semibold mb-1">
                    Add skill (step)
                  </div>

                  {/* Row 1: code + age */}
                  <div className="grid grid-cols-2 gap-1 mb-1">
                    <div>
                      <input
                        className="border rounded px-1 py-1 text-[11px] w-full"
                        placeholder="code (e.g. C1)"
                        value={newSkill.code}
                        onChange={(e) =>
                          handleNewSkillChange(
                            'code',
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div>
                      <input
                        className="border rounded px-1 py-1 text-[11px] w-full"
                        placeholder="Age range (e.g. 0–3m)"
                        value={newSkill.age_range}
                        onChange={(e) =>
                          handleNewSkillChange(
                            'age_range',
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </div>

                  {/* Row 2: name */}
                  <input
                    className="border rounded px-1 py-1 text-[11px] w-full mb-1"
                    placeholder="Name (short title)"
                    value={newSkill.name}
                    onChange={(e) =>
                      handleNewSkillChange(
                        'name',
                        e.target.value
                      )
                    }
                  />

                  {/* Description + order */}
                  <textarea
                    className="border rounded px-1 py-1 text-[11px] w-full mb-1"
                    placeholder="Description (optional)"
                    value={newSkill.description}
                    onChange={(e) =>
                      handleNewSkillChange(
                        'description',
                        e.target.value
                      )
                    }
                  />
                  <div className="flex items-center gap-2">
                    <input
                      className="border rounded px-1 py-1 text-[11px] w-20"
                      placeholder="Order"
                      value={newSkill.position_order}
                      onChange={(e) =>
                        handleNewSkillChange(
                          'position_order',
                          e.target.value
                        )
                      }
                    />
                    <button
                      className="ml-auto text-[11px] px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                      onClick={handleAddSkill}
                      disabled={skillSavingId === -1}
                    >
                      {skillSavingId === -1
                        ? 'Adding...'
                        : 'Add'}
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {skills.length === 0 &&
                    !skillsLoading && (
                      <p className="text-xs text-gray-500">
                        No skills yet. Add skill steps for this
                        domain.
                      </p>
                    )}

                  {skills.map((skill) => (
                    <div
                      key={skill.id}
                      className="border rounded px-2 py-2 mb-2 text-xs bg-white shadow-sm"
                    >
                      {/* Row 1: code + age */}
                      <div className="flex items-center gap-2 mb-1">
                        <div>
                          <label className="text-[10px] text-gray-500 block mb-0.5">
                            Code
                          </label>
                          <input
                            className="border rounded px-1 py-0.5 text-[11px] w-20"
                            value={skill.code}
                            onChange={(e) =>
                              handleSkillChange(
                                skill.id,
                                'code',
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-gray-500 block mb-0.5">
                            Age
                          </label>
                          <input
                            className="border rounded px-1 py-0.5 text-[11px] w-24"
                            value={skill.age_range || ''}
                            placeholder="Age"
                            onChange={(e) =>
                              handleSkillChange(
                                skill.id,
                                'age_range',
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>

                      {/* Row 2: name + order */}
                      <div className="flex items-start gap-2 mb-1">
                        <div className="flex-1">
                          <label className="text-[10px] text-gray-500 block mb-0.5">
                            Name
                          </label>
                          <input
                            className="border rounded px-1 py-0.5 text-[11px] w-full"
                            value={skill.name}
                            onChange={(e) =>
                              handleSkillChange(
                                skill.id,
                                'name',
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="w-16">
                          <label className="text-[10px] text-gray-500 block mb-0.5">
                            Order
                          </label>
                          <input
                            className="border rounded px-1 py-0.5 text-[11px] w-full"
                            value={skill.position_order || ''}
                            placeholder="#"
                            onChange={(e) =>
                              handleSkillChange(
                                skill.id,
                                'position_order',
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>

                      {/* Description */}
                      <div className="mb-1">
                        <label className="text-[10px] text-gray-500 block mb-0.5">
                          Description
                        </label>
                        <textarea
                          className="border rounded px-1 py-0.5 text-[11px] w-full"
                          value={skill.description || ''}
                          onChange={(e) =>
                            handleSkillChange(
                              skill.id,
                              'description',
                              e.target.value
                            )
                          }
                          placeholder="Description"
                        />
                      </div>

                      <div className="flex justify-end gap-2 mt-1">
                        <button
                          className="text-[11px] px-2 py-0.5 rounded border border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-60"
                          onClick={() => askDeleteSkill(skill)}
                          disabled={skillSavingId === skill.id}
                        >
                          Delete
                        </button>
                        <button
                          className="text-[11px] px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                          onClick={() =>
                            handleSaveSkillRow(skill)
                          }
                          disabled={
                            skillSavingId === skill.id
                          }
                        >
                          {skillSavingId === skill.id
                            ? 'Saving...'
                            : 'Save'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Confirm delete modal */}
      {confirmDelete.open && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-4 w-full max-w-md">
            <h3 className="text-sm font-semibold mb-2">
              {confirmTitle}
            </h3>
            <p className="text-xs text-gray-600 mb-4">
              {confirmText}
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="text-xs px-3 py-1 rounded border border-gray-300 hover:bg-gray-50"
                onClick={() =>
                  setConfirmDelete({
                    open: false,
                    type: null,
                    target: null,
                  })
                }
              >
                Cancel
              </button>
              <button
                className="text-xs px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview tree modal */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg shadow-lg p-4 w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-semibold">
                  Discipline tree preview
                </h3>
                {disciplineForm.name && (
                  <p className="text-[11px] text-gray-600">
                    {disciplineForm.name} ({disciplineForm.service_type}) –{' '}
                    {disciplineForm.organization_id
                      ? renderOrgLabel(disciplineForm)
                      : 'VisitOMS global template'}
                  </p>
                )}
              </div>
              <button
                className="text-xs px-3 py-1 rounded border border-gray-300 hover:bg-gray-50"
                onClick={() => setPreviewOpen(false)}
              >
                Close
              </button>
            </div>

            {previewLoading && (
              <p className="text-xs text-gray-500">
                Building tree...
              </p>
            )}

            {previewError && (
              <p className="text-xs text-red-600 mb-2">
                {previewError}
              </p>
            )}

            {!previewLoading && !previewError && (
              <div className="flex-1 overflow-y-auto mt-1">
                {previewTree.length === 0 && (
                  <p className="text-xs text-gray-500">
                    No domains/skills to display.
                  </p>
                )}

                <ul className="text-xs space-y-2">
                  {previewTree.map((dom) => (
                    <li key={dom.id}>
                      <div className="font-semibold">
                        • {dom.name} ({dom.code}){' '}
                        <span className="text-[10px] text-gray-500">
                          – {dom.skills?.length || 0} skills
                        </span>
                      </div>
                      {dom.skills && dom.skills.length > 0 && (
                        <ul className="ml-4 mt-1 space-y-0.5">
                          {dom.skills
                            .slice()
                            .sort(
                              (a, b) =>
                                (a.position_order || 0) -
                                (b.position_order || 0)
                            )
                            .map((s) => (
                              <li key={s.id}>
                                <span className="font-mono">
                                  {s.code}
                                </span>{' '}
                                – {s.name}{' '}
                                {s.age_range && (
                                  <span className="text-[10px] text-gray-500">
                                    ({s.age_range})
                                  </span>
                                )}
                              </li>
                            ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperDisciplines;



